import type {TokenClaimsSerializer, UserDetailSerializer} from "@/generated/DjangoTypes";
import {useIsLoggedIn} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {isFinishRegistrationModalVisibleAtom} from "@/store/jotaiAtoms";
import {useLocation, useNavigate, useSearch} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import {jwtDecode} from "jwt-decode";
import {useAsync} from "react-use";

const ROUTES_WITH_ACCESS_TOKEN = ["/verify-email", "/welcome"] as const;

const useFetchUserFromAuthToken = (callback?: (user: UserDetailSerializer) => void) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {access_token} = useSearch({strict: false});
  const setIsFinishRegistrationModalVisible = useSetAtom(isFinishRegistrationModalVisibleAtom);
  const isLoggedIn = useIsLoggedIn();

  const {loading, value: accessToken} = useAsync(async () => {
    if (!ROUTES_WITH_ACCESS_TOKEN.find(url => location.pathname.includes(url)) || isLoggedIn) {
      return;
    }

    if (!access_token) {
      setIsFinishRegistrationModalVisible(false);
      return;
    }

    try {
      const userId = jwtDecode<TokenClaimsSerializer>(access_token).user_id;
      const response = await client.get(`/users/${userId}/`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      callback?.(response.data);
      return access_token;
    } catch (err) {
      setIsFinishRegistrationModalVisible(false);
      navigate({to: "/login"});
      throw err;
    }
  }, [access_token, navigate, location.pathname, isLoggedIn]);

  return {isLoading: loading, accessToken};
};

export default useFetchUserFromAuthToken;
